import { useEffect, useMemo, useState } from 'react';
import { Check, Database, Edit3, Image, LogOut, Plus, RefreshCw, Save, Search, Shield, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import Brand from './Brand';
import {
  deleteRestaurant,
  fetchAllRestaurants,
  firebaseConfigured,
  getAdminSession,
  saveRestaurant,
  signInAdmin,
  signOutAdmin,
} from '../lib/firebaseRest';
import { enrichRestaurants, refreshReferenceManifest, seedRestaurants } from '../lib/repository';

const emptyRestaurant = {
  id: '', name: '', nameAr: '', aliases: [], menuUrl: '', location: '', phone: '',
  category: '', description: '', coverImage: '', active: true, order: 0,
};

function Editor({ value, onClose, onSaved, session }) {
  const [form, setForm] = useState({ ...emptyRestaurant, ...value });
  const [busy, setBusy] = useState(false);
  const isNew = !value?.id;

  function change(field, next) { setForm((current) => ({ ...current, [field]: next })); }

  async function submit(event) {
    event.preventDefault();
    if (!form.id.trim() || !form.name.trim()) return toast.error('Document ID and English name are required.');
    if (!/^[a-z0-9-]+$/.test(form.id)) return toast.error('Use lowercase letters, numbers, and dashes for the document ID.');
    setBusy(true);
    try {
      const saved = await saveRestaurant({
        ...form,
        aliases: typeof form.aliases === 'string'
          ? form.aliases.split(',').map((item) => item.trim()).filter(Boolean)
          : form.aliases,
      }, session);
      toast.success('Restaurant saved.');
      onSaved(saved);
    } catch (error) { toast.error(error.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop">
      <form className="editor-modal" onSubmit={submit}>
        <div className="modal-header"><div><span className="eyebrow">Restaurant record</span><h2>{isNew ? 'Add restaurant' : 'Edit restaurant'}</h2></div><button className="icon-button" type="button" onClick={onClose}><X /></button></div>
        <div className="form-grid">
          <label><span>Document ID / image folder</span><input value={form.id} onChange={(e) => change('id', e.target.value.toLowerCase().replace(/\s+/g, '-'))} disabled={!isNew} placeholder="restaurant-name" /></label>
          <label><span>Order</span><input type="number" value={form.order} onChange={(e) => change('order', Number(e.target.value))} /></label>
          <label><span>English name</span><input value={form.name} onChange={(e) => change('name', e.target.value)} /></label>
          <label><span>Arabic name</span><input dir="rtl" value={form.nameAr} onChange={(e) => change('nameAr', e.target.value)} /></label>
          <label className="wide"><span>Aliases, separated by commas</span><input value={Array.isArray(form.aliases) ? form.aliases.join(', ') : form.aliases} onChange={(e) => change('aliases', e.target.value)} /></label>
          <label className="wide"><span>Menu URL</span><input type="url" value={form.menuUrl} onChange={(e) => change('menuUrl', e.target.value)} placeholder="https://..." /></label>
          <label><span>Location</span><input value={form.location} onChange={(e) => change('location', e.target.value)} /></label>
          <label><span>Phone</span><input value={form.phone} onChange={(e) => change('phone', e.target.value)} /></label>
          <label><span>Category</span><input value={form.category} onChange={(e) => change('category', e.target.value)} /></label>
          <label><span>Cover image URL</span><input value={form.coverImage} onChange={(e) => change('coverImage', e.target.value)} placeholder={`/reference-images/${form.id || 'restaurant-id'}/front.jpg`} /></label>
          <label className="wide"><span>Description</span><textarea value={form.description} onChange={(e) => change('description', e.target.value)} rows="3" /></label>
          <label className="toggle-label"><input type="checkbox" checked={form.active} onChange={(e) => change('active', e.target.checked)} /><i>{form.active ? <Check size={15} /> : null}</i><span>Visible in the public app</span></label>
        </div>
        <div className="folder-hint"><Image size={18} /><div><strong>Reference image folder</strong><code>public/reference-images/{form.id || 'restaurant-id'}/</code><span>Add 3–8 storefront and sign photos. On cPanel, press Refresh—no rebuild is required.</span></div></div>
        <div className="modal-actions"><button type="button" className="secondary-button" onClick={onClose}>Cancel</button><button className="primary-button" disabled={busy}><Save size={17} /> {busy ? 'Saving...' : 'Save restaurant'}</button></div>
      </form>
    </div>
  );
}

export default function AdminPanel({ onExit }) {
  const [session, setSession] = useState(null);
  const [checking, setChecking] = useState(true);
  const [restaurants, setRestaurants] = useState([]);
  const [query, setQuery] = useState('');
  const [editor, setEditor] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { getAdminSession().then((value) => { setSession(value); setChecking(false); }); }, []);

  async function load(sessionValue = session) {
    if (!sessionValue) return;
    setLoading(true);
    try {
      await refreshReferenceManifest();
      setRestaurants(enrichRestaurants(await fetchAllRestaurants(sessionValue)));
    }
    catch (error) { toast.error(error.message); }
    finally { setLoading(false); }
  }

  useEffect(() => {
    if (!session) return;
    // The authenticated session is the external trigger for the first Firestore load.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load(session);
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const needle = query.toLowerCase().trim();
    if (!needle) return restaurants;
    return restaurants.filter((item) => `${item.name} ${item.nameAr} ${item.location} ${item.category}`.toLowerCase().includes(needle));
  }, [restaurants, query]);

  async function login(event) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    try { setSession(await signInAdmin(data.get('email'), data.get('password'))); toast.success('Signed in.'); }
    catch (error) { toast.error(error.message); }
  }

  async function remove(item) {
    if (!window.confirm(`Delete ${item.name}?`)) return;
    try { await deleteRestaurant(item.id, session); setRestaurants((current) => current.filter((entry) => entry.id !== item.id)); toast.success('Restaurant deleted.'); }
    catch (error) { toast.error(error.message); }
  }

  async function importSeed() {
    setLoading(true);
    try {
      for (const item of seedRestaurants) await saveRestaurant(item, session);
      toast.success('Bundled demo records imported.');
      await load();
    } catch (error) { toast.error(error.message); }
    finally { setLoading(false); }
  }

  if (checking) return <div className="admin-loading"><RefreshCw className="spin" /></div>;
  if (!firebaseConfigured) return (
    <div className="admin-shell page-frame">
      <header className="simple-header"><Brand /><button className="text-button" onClick={onExit}>Back to app</button></header>
      <main className="setup-card"><Database /><span className="eyebrow">Firebase setup required</span><h1>Connect your database</h1><p>Copy <code>.env.example</code> to <code>.env</code>, paste your Firebase Web App API key and Project ID, then restart the development server.</p><pre>VITE_FIREBASE_API_KEY=...{`\n`}VITE_FIREBASE_PROJECT_ID=...</pre><p>Deploy the included <code>firestore.rules</code>, enable Email/Password Authentication, and create an admin document at <code>admins/YOUR_USER_UID</code>.</p></main>
    </div>
  );
  if (!session) return (
    <div className="login-page">
      <button className="back-link" onClick={onExit}>← Back to app</button>
      <form className="login-card" onSubmit={login}>
        <Brand /><span className="admin-lock"><Shield /></span><h1>Admin dashboard</h1><p>Manage Scan & View restaurant information and menu destinations stored in Firebase.</p>
        <label><span>Email</span><input type="email" name="email" required autoComplete="email" /></label>
        <label><span>Password</span><input type="password" name="password" required autoComplete="current-password" /></label>
        <button className="primary-button">Sign in</button>
      </form>
    </div>
  );

  return (
    <div className="admin-shell page-frame">
      <header className="admin-header"><Brand /><div><span>{session.email}</span><button className="text-button" onClick={() => { signOutAdmin(); setSession(null); }}><LogOut size={17} /> Sign out</button></div></header>
      <main className="admin-main">
        <section className="admin-title"><div><span className="eyebrow">Firebase directory</span><h1>Restaurant management</h1><p>Firebase stores restaurant details. The live server folder scanner discovers recognition images automatically.</p></div><button className="primary-button" onClick={() => setEditor(emptyRestaurant)}><Plus size={18} /> Add restaurant</button></section>
        <section className="stat-grid"><article><Database /><div><strong>{restaurants.length}</strong><span>Total records</span></div></article><article><Check /><div><strong>{restaurants.filter((item) => item.active).length}</strong><span>Published</span></div></article><article><Image /><div><strong>{restaurants.reduce((sum, item) => sum + item.referenceImages.length, 0)}</strong><span>Local references</span></div></article></section>
        <section className="admin-toolbar"><label><Search /><input placeholder="Search restaurants..." value={query} onChange={(e) => setQuery(e.target.value)} /></label><button className="secondary-button" onClick={() => load()}><RefreshCw className={loading ? 'spin' : ''} size={17} /> Refresh</button>{!restaurants.length && <button className="secondary-button" onClick={importSeed}>Import demo records</button>}</section>
        <section className="restaurant-table">
          <div className="table-head"><span>Restaurant</span><span>Status</span><span>References</span><span>Menu</span><span>Actions</span></div>
          {filtered.map((item) => <article className="table-row" key={item.id}><div className="restaurant-cell">{item.coverImage ? <img src={item.coverImage} alt="" /> : <span className="image-placeholder"><Image /></span>}<div><strong>{item.name}</strong><span dir="auto">{item.nameAr || item.location || item.id}</span><code>{item.id}</code></div></div><span className={`status-chip ${item.active ? 'active' : ''}`}>{item.active ? 'Published' : 'Hidden'}</span><span>{item.referenceImages.length} images</span><span className={item.menuUrl ? 'menu-ready' : 'menu-missing'}>{item.menuUrl ? 'Connected' : 'Missing'}</span><div className="row-actions"><button onClick={() => setEditor(item)}><Edit3 /></button><button className="danger" onClick={() => remove(item)}><Trash2 /></button></div></article>)}
          {!filtered.length && <div className="table-empty">No restaurants match this search.</div>}
        </section>
      </main>
      {editor && <Editor value={editor} session={session} onClose={() => setEditor(null)} onSaved={() => { setEditor(null); load(); }} />}
    </div>
  );
}
